import * as React from 'react';
import { useState, useEffect } from 'react';
import { supabaseClient } from '../utils/supabaseClient';
import { backendUrl } from '../utils/socketClient';
import ReviewTable from './ReviewTable';
import ReviewModal from './ReviewModal';
import CustomReplyModal from './CustomReplyModal';
import SettingsPanel from './SettingsPanel';
import FeedbackModal from './FeedbackModal';
import StoreSelectionModal from './StoreSelectionModal';
import { Settings, Filter, RefreshCw, MessageSquarePlus, User, Lock, KeyRound, ArrowLeft, BarChart2, Link2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface MainPageProps {
  session: any;
  onLogout: () => void;
}

const MainPage: React.FC<MainPageProps> = ({ session, onLogout }) => {
  const [stores, setStores] = useState<{ shopname: string }[]>([]);
  const [selectedStore, setSelectedStore] = useState<string>('');
  const [reviews, setReviews] = useState<any[]>([]);
  const [selectedReview, setSelectedReview] = useState<any>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [sortOption, setSortOption] = useState('newest');
  const [filterOption, setFilterOption] = useState('all');
  const [presetTexts, setPresetTexts] = useState<string[]>([]);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [gbpStatus, setGbpStatus] = useState<{ connected: boolean; google_email?: string } | null>(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [amount, setAmount] = useState<number | null>(null);
  const [showStoreSelectionModal, setShowStoreSelectionModal] = useState(false);
  const [newPlanAmount, setNewPlanAmount] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'ダッシュボード | RepliGo（レプリゴ）';

    // URLのクエリパラメータをチェック
    const searchParams = new URLSearchParams(window.location.search);
    const mode = searchParams.get('mode');
    
    if (mode === 'reset') {
      setShowResetForm(true);
    }
  }, []);

  // Load stores for the logged-in user
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const { data, error } = await supabaseClient
          .from('stores')
          .select('shopname, user_email')
          .eq('user_email', session.user.email);
        
        if (error) {
          console.error("Error fetching stores:", error.message);
          setStores([]);
          return;
        }

        if (data && data.length > 0) {
          // console.log('Fetched stores:', data);
          setStores(data);
          
          // localStorageから保存された店舗を取得
          const savedStore = localStorage.getItem('selectedStore');
          
          // 保存された店舗が存在し、かつ現在の店舗リストに含まれている場合のみ使用
          if (savedStore && data.some(store => store.shopname === savedStore)) {
            setSelectedStore(savedStore);
          } else {
            // 保存された店舗が存在しないか、リストにない場合は最初の店舗を選択
            setSelectedStore(data[0].shopname);
            localStorage.setItem('selectedStore', data[0].shopname);
          }
        } else {
          // console.log('No stores found for user');
          setStores([]);
          setSelectedStore('');
          localStorage.removeItem('selectedStore');
        }
      } catch (error) {
        console.error('Error in fetchStores:', error);
        setStores([]);
        setSelectedStore('');
      }
    };
    
    fetchStores();
  }, [session]);

  // 店舗選択が変更されたときにlocalStorageに保存
  useEffect(() => {
    if (selectedStore) {
      localStorage.setItem('selectedStore', selectedStore);
      // console.log('Selected store updated:', selectedStore);
    }
  }, [selectedStore]);

  // Load settings and reviews when store changes
  useEffect(() => {
    if (selectedStore) {
      loadSettings(selectedStore);
      loadReviews();
    }
  }, [selectedStore]);

  // GBP接続状態の取得
  useEffect(() => {
    const fetchGbpStatus = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/gbp/status?user_email=${encodeURIComponent(session.user.email)}`);
        if (response.ok) {
          const data = await response.json();
          setGbpStatus(data);
        }
      } catch (error) {
        console.error('Error fetching GBP status:', error);
      }
    };

    fetchGbpStatus();

    // Google OAuth完了メッセージを受信
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'GOOGLE_OAUTH_SUCCESS') {
        fetchGbpStatus();
        loadReviews();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [session.user.email]);

  const loadSettings = (shopName: string) => {
    if (!shopName) return;

    const url = `${backendUrl}/reply_settings/${encodeURIComponent(shopName)}`;
    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error("HTTP error " + response.status);
        }
        return response.json();
      })
      .then(data => {
        setPresetTexts(data.preset_texts || []);
        setKeywords(data.keywords || []);
      })
      .catch(error => {
        console.error("Error loading settings:", error);
      });
  };

  const loadReviews = () => {
    if (!selectedStore) return;

    const encodedStoreName = encodeURIComponent(selectedStore);
    const reviewsUrl = `${backendUrl}/reviews/${encodedStoreName}?sort=${sortOption}&filter=${filterOption}`;

    // console.log('Loading reviews for store:', {
    //   selectedStore,
    //   encodedStoreName,
    //   reviewsUrl
    // });

    fetch(reviewsUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error("HTTP error " + response.status);
        }
        return response.json();
      })
      .then(data => {
        // console.log('Received reviews data:', {
        //   shopname: data.shopname,
        //   reviewCount: data.reviews?.length
        // });
        setReviews(data.reviews || []);
      })
      .catch(error => {
        console.error("Error loading reviews:", error);
      });
  };

  // GBP APIから口コミを同期
  const syncFromGBP = async () => {
    if (!selectedStore) {
      alert("店舗が選択されていません");
      return;
    }

    if (!gbpStatus?.connected) {
      alert("Googleアカウントが連携されていません。設定からGoogleアカウントを連携してください。");
      return;
    }

    setIsSyncing(true);
    try {
      const response = await fetch(`${backendUrl}/api/gbp/sync-reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: session.user.email,
          shop_name: selectedStore
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to sync reviews');
      }

      const result = await response.json();
      alert(`口コミを同期しました（${result.synced_count}件）`);
      loadReviews();
    } catch (error: any) {
      console.error("Error syncing reviews:", error);
      alert(error.message);
    } finally {
      setIsSyncing(false);
    }
  };

  // Google OAuth開始
  const startGoogleOAuth = () => {
    const authUrl = `${backendUrl}/auth/google/start?user_email=${encodeURIComponent(session.user.email)}`;
    window.open(authUrl, 'GoogleOAuth', 'width=600,height=700');
  };

  // Google連携解除
  const disconnectGoogle = async () => {
    if (!confirm('Googleアカウントの連携を解除しますか？')) return;

    setIsDisconnecting(true);
    try {
      const response = await fetch(`${backendUrl}/api/gbp/disconnect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: session.user.email })
      });

      if (response.ok) {
        setGbpStatus({ connected: false });
        alert('Googleアカウントの連携を解除しました');
      } else {
        const data = await response.json();
        alert(data.error || '連携解除に失敗しました');
      }
    } catch (error) {
      console.error('Error disconnecting Google:', error);
      alert('連携解除中にエラーが発生しました');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const displayReviews = () => {
    loadReviews();
  };

  const handleReviewClick = (review: any) => {
    setSelectedReview(review);
    setShowReviewModal(true);
  };

  const saveSettings = (newPresets: string[], newKeywords: string[]) => {
    if (!selectedStore) {
      alert("店舗が選択されていません");
      return;
    }

    const encodedStoreName = encodeURIComponent(selectedStore);
    fetch(`${backendUrl}/reply_settings/${encodedStoreName}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        preset_texts: newPresets,
        keywords: newKeywords
      })
    })
      .then(response => {
        if (!response.ok) {
          throw new Error("HTTP error " + response.status);
        }
        return response.json();
      })
      .then(() => {
        setPresetTexts(newPresets);
        setKeywords(newKeywords);
        alert("設定が保存されました");
      })
      .catch(error => {
        console.error("Error saving settings:", error);
        alert("設定保存中にエラーが発生しました");
      });
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabaseClient.auth.signOut();
      if (error) throw error;
      onLogout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // メニュー外クリックでドロップダウンを閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword.trim() || !confirmNewPassword.trim()) {
      setMessage("新しいパスワードを入力してください");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setMessage("パスワードが一致しません");
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const { error } = await supabaseClient.auth.updateUser({
        password: newPassword
      });

      if (error) {
        setMessage(error.message);
      } else {
        setMessage("パスワードを変更しました。");
        setTimeout(() => {
          window.location.href = '/app';
        }, 2000);
      }
    } catch (error) {
      setMessage("パスワード変更処理中にエラーが発生しました");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openAnalysisPage = () => {
    if (selectedStore) {
      const analysisUrl = `/analysis?store=${encodeURIComponent(selectedStore)}`;
      window.open(analysisUrl, '_blank');
    }
  };

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const { data: paymentData, error: paymentError } = await supabaseClient
          .from('payments')
          .select('status, amount')
          .eq('email', session.user.email)
          .single();

        if (paymentError) {
          console.error('Error checking payment status:', paymentError);
          return;
        }

        if (paymentData.status !== 'active') {
          // 支払いが無効な場合、scan_settingsのnotify_emailを削除
          const { error: updateError } = await supabaseClient
            .from('scan_settings')
            .delete()
            .eq('notify_email', session.user.email);

          if (updateError) {
            console.error('Error deleting scan settings:', updateError);
          }

          alert('サブスクリプションが無効になりました。Stripeの顧客ポータルで支払い状況を確認してください。', 'error');
          await supabaseClient.auth.signOut();
          navigate('/login');
          return;
        }

        // 店舗数に基づくプラン制限チェック
        const maxStores = paymentData.amount === 1500 ? 1 : paymentData.amount === 3000 ? 5 : Infinity;
        
        if (stores.length > maxStores) {
          // console.log('店舗数が制限を超えています:', { 
          //   stores: stores.length, 
          //   max: maxStores, 
          //   plan: paymentData.amount 
          // });
          setShowStoreSelectionModal(true);
          setNewPlanAmount(paymentData.amount);
        }

        setAmount(paymentData.amount);
      } catch (error) {
        console.error('Error in checkStatus:', error);
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 60000);
    return () => clearInterval(interval);
  }, [session.user.email, stores.length, navigate]);

  // プラン変更時の店舗選択処理
  const handlePlanChange = async (newAmount: number) => {
    const maxStores = newAmount === 1500 ? 1 : newAmount === 3000 ? 5 : Infinity;
    
    if (stores.length > maxStores) {
      setShowStoreSelectionModal(true);
      setNewPlanAmount(newAmount);
    } else {
      // 店舗数が制限内の場合は直接プラン変更を実行
      await updatePlan(newAmount);
    }
  };

  // 選択された店舗の処理
  const handleStoreSelection = async (selectedStores: string[]) => {
    try {
      // 選択されなかった店舗を削除
      const storesToDelete = stores
        .filter(store => !selectedStores.includes(store.shopname))
        .map(store => store.shopname);

      if (storesToDelete.length > 0) {
        // 店舗名とメールアドレスの両方で絞り込んで削除
        const { error } = await supabaseClient
          .from('stores')
          .delete()
          .in('shopname', storesToDelete)
          .eq('user_email', session.user.email);

        if (error) {
          console.error('Error deleting stores:', error);
          alert('店舗の削除中にエラーが発生しました。', 'error');
          return;
        }
      }

      // プランを更新
      await updatePlan(newPlanAmount);

      // モーダルを閉じる
      setShowStoreSelectionModal(false);
      setNewPlanAmount(null);

      // ページをリロード
      window.location.reload();
    } catch (error) {
      console.error('Error handling store selection:', error);
      alert('処理中にエラーが発生しました。', 'error');
    }
  };

  // プラン更新処理
  const updatePlan = async (newAmount: number | null) => {
    if (!newAmount) return;
    try {
      const { error } = await supabaseClient
        .from('payments')
        .update({ amount: newAmount })
        .eq('email', session.user.email);

      if (error) {
        console.error('Error updating plan:', error);
        alert('プランの更新中にエラーが発生しました。', 'error');
        return;
      }

      alert('プランが更新されました。', 'success');
    } catch (error) {
      console.error('Error updating plan:', error);
      alert('プランの更新中にエラーが発生しました。', 'error');
    }
  };

  if (showResetForm) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100 p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl shadow-xl overflow-hidden">
            <div className="bg-[#007AFF] p-6 text-center">
              <img src="/img/logo-white.png" alt="RepliGo（レプリゴ）" className="h-12 mx-auto" />
            </div>
            
            <form onSubmit={handleUpdatePassword} className="p-8 space-y-6">
              <div className="space-y-2">
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                  新しいパスワード
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-[#007AFF]"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="confirmNewPassword" className="block text-sm font-medium text-gray-700">
                  新しいパスワード（確認）
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="confirmNewPassword"
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-[#007AFF]"
                  />
                </div>
              </div>
              
              {message && (
                <div className={`text-sm font-medium ${message.includes('変更しました') ? 'text-green-600' : 'text-red-600'}`}>
                  {message}
                </div>
              )}
              
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => window.location.href = '/app'}
                  className="flex-1 flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF]"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  戻る
                </button>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#007AFF] hover:bg-[#0066CC] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      処理中...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <KeyRound className="mr-2 h-4 w-4" />
                      パスワードを変更
                    </span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <img src="/img/logo.png" alt="RepliGo（レプリゴ）" className="h-12" />
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">
              {session.user.email}
            </span>
            <div className="relative user-menu-container">
              <button 
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF]"
              >
                <User className="h-6 w-6 text-gray-600" />
              </button>
              
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-1">
                    <div className="px-4 py-2 text-sm text-gray-500 border-b">
                      現在のプラン: {amount === 0 ? "無料" : amount === 3000 ? "スタンダード" : amount === 5000 ? "プレミアム" : "無料"}
                    </div>
                    <a
                      href={import.meta.env.VITE_STRIPE_CUSTOMER_PORTAL_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                    >
                      プラン変更
                    </a>
                    <button
                      onClick={() => {
                        setShowFeedbackModal(true);
                        setShowUserMenu(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                    >
                      ご意見・ご要望
                    </button>
                    <button
                      onClick={() => {
                        window.open("https://repligo.forceai.jp/help", "_blank");
                        setShowUserMenu(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                    >
                      ヘルプ
                    </button>
                    {gbpStatus?.connected && (
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          disconnectGoogle();
                        }}
                        disabled={isDisconnecting}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 disabled:opacity-50"
                        role="menuitem"
                      >
                        {isDisconnecting ? 'Google連携解除中...' : 'Google連携を解除'}
                      </button>
                    )}
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                    >
                      ログアウト
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {stores.length > 0 ? (
          <div className="bg-white rounded-lg shadow p-6">
            {/* Store Selection and Actions */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                <div className="flex items-center space-x-2">
                  <label htmlFor="storeSelect" className="text-sm font-medium text-gray-700">
                    店舗選択:
                  </label>
                  <select
                    id="storeSelect"
                    value={selectedStore}
                    onChange={(e) => setSelectedStore(e.target.value)}
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#007AFF] focus:border-[#007AFF] sm:text-sm rounded-md"
                  >
                    {stores.map((store, index) => (
                      <option key={index} value={store.shopname}>
                        {store.shopname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {/* Google連携ボタン */}
                {gbpStatus?.connected ? (
                  <button
                    onClick={syncFromGBP}
                    disabled={isSyncing || !selectedStore}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#007AFF] hover:bg-[#0066CC] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSyncing ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        同期中...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <RefreshCw className="mr-2 h-4 w-4" />
                        口コミ同期
                      </span>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={startGoogleOAuth}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <Link2 className="mr-2 h-4 w-4" />
                    Googleアカウント連携
                  </button>
                )}

                <button
                  onClick={displayReviews}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#007AFF] hover:bg-[#007AFF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF]"
                >
                  <Filter className="mr-2 h-4 w-4" />
                  口コミ表示
                </button>
                
                <button
                  onClick={() => setShowCustomModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <MessageSquarePlus className="mr-2 h-4 w-4" />
                  返信自由生成
                </button>
                
                <button
                  onClick={openAnalysisPage}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#007AFF] hover:bg-[#007AFF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF]"
                >
                  <BarChart2 className="mr-2 h-4 w-4" />
                  分析
                </button>
                
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF]"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  設定
                </button>
              </div>
            </div>

            {/* Settings Panel */}
            {showSettings && (
              <SettingsPanel
                presetTexts={presetTexts}
                keywords={keywords}
                onSave={saveSettings}
                onClose={() => setShowSettings(false)}
                storeName={selectedStore}
                user={session.user}
              />
            )}

            {/* Filters */}
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 p-4 bg-gray-50 rounded-md">
              <div className="flex items-center space-x-2">
                <label htmlFor="sortOption" className="text-sm font-medium text-gray-700">
                  並べ替え:
                </label>
                <select
                  id="sortOption"
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#007AFF] focus:border-[#007AFF] sm:text-sm rounded-md"
                >
                  <option value="newest">新しい順</option>
                  <option value="highest">評価の高い順</option>
                  <option value="lowest">評価の低い順</option>
                </select>
              </div>
              
              <div className="flex items-center space-x-2">
                <label htmlFor="filterOption" className="text-sm font-medium text-gray-700">
                  絞り込み:
                </label>
                <select
                  id="filterOption"
                  value={filterOption}
                  onChange={(e) => setFilterOption(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#007AFF] focus:border-[#007AFF] sm:text-sm rounded-md"
                >
                  <option value="all">全て</option>
                  <option value="owner">オーナー返信あり</option>
                  <option value="no_owner">オーナー返信無し</option>
                </select>
              </div>
              
              <button
                onClick={displayReviews}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#007AFF] hover:bg-[#007AFF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF]"
              >
                適用
              </button>
            </div>

            {/* Reviews Table */}
            <ReviewTable 
              reviews={reviews} 
              onReviewClick={handleReviewClick} 
            />
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="mb-4 text-gray-600">店舗が登録されていません。Googleアカウントを連携してください。</p>
            <button
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              onClick={startGoogleOAuth}
            >
              Googleアカウント連携
            </button>
          </div>
        )}
      </main>

      {/* Review Modal */}
      {showReviewModal && selectedReview && (
        <ReviewModal
          review={selectedReview}
          storeName={selectedStore}
          userEmail={session.user.email}
          onClose={() => setShowReviewModal(false)}
        />
      )}

      {/* Custom Reply Modal */}
      {showCustomModal && (
        <CustomReplyModal
          storeName={selectedStore}
          onClose={() => setShowCustomModal(false)}
        />
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <FeedbackModal
          userEmail={session.user.email}
          onClose={() => setShowFeedbackModal(false)}
        />
      )}

      {showStoreSelectionModal && newPlanAmount && (
        <StoreSelectionModal
          stores={stores}
          maxStores={newPlanAmount === 1500 ? 1 : 5}
          onClose={() => {
            setShowStoreSelectionModal(false);
            setNewPlanAmount(null);
          }}
          onConfirm={handleStoreSelection}
        />
      )}

    </div>
  );
};

export default MainPage;
